import { CROSSED, EMPTY, FILLED, MAX_SIZE, type CellState } from "./types";
import type { PicrossDefinition, PicrossProgress, PicrossSubmission } from "./types";

const DEFAULT_RULES = {
  en: "Fill cells so that every row and column shows the runs of filled cells given by its clues, in order, with at least one empty cell between two runs.",
  fr: "Noircissez des cases de sorte que chaque ligne et chaque colonne présente les blocs de cases noircies indiqués par ses indices, dans l'ordre, avec au moins une case vide entre deux blocs.",
};

export function emptyDefinition(): PicrossDefinition {
  return {
    puzzleType: "picross",
    typeVersion: 1,
    rows: 5,
    cols: 5,
    clues: { rows: Array.from({ length: 5 }, () => []), cols: Array.from({ length: 5 }, () => []) },
    rulesText: { ...DEFAULT_RULES },
  };
}

export function clampSize(value: number): number {
  if (!Number.isFinite(value)) return 1;
  return Math.min(MAX_SIZE, Math.max(1, Math.trunc(value)));
}

export function blankRows(rows: number, cols: number, fill: CellState = EMPTY): string[] {
  return Array.from({ length: rows }, () => fill.repeat(cols));
}

/** Crops or pads a row set to the given size, keeping the top-left corner. */
export function resizeRows(source: string[], rows: number, cols: number): string[] {
  return Array.from({ length: rows }, (_, r) => {
    const row = source[r] ?? "";
    return (
      row.length >= cols ? row.slice(0, cols) : row + EMPTY.repeat(cols - row.length)
    ) as string;
  });
}

function sanitizeRow(raw: unknown, allowed: readonly string[]): string {
  if (typeof raw !== "string") return "";
  return Array.from(raw, (char) => (allowed.includes(char) ? char : EMPTY)).join("");
}

export function emptyProgress(definition: PicrossDefinition): PicrossProgress {
  return { version: 1, rows: blankRows(definition.rows, definition.cols) };
}

/** Accepts any stored blob (older versions, null, garbage) and returns a valid progress. */
export function migrateProgress(definition: PicrossDefinition, raw: unknown): PicrossProgress {
  if (typeof raw !== "object" || raw === null || Array.isArray(raw))
    return emptyProgress(definition);
  const source = (raw as { rows?: unknown }).rows;
  if (!Array.isArray(source)) return emptyProgress(definition);
  const rows = source.map((row) => sanitizeRow(row, [FILLED, EMPTY, CROSSED]));
  return { version: 1, rows: resizeRows(rows, definition.rows, definition.cols) };
}

/** The server only reads filled and empty: crosses are the player's own notes. */
export function serializeSubmission(
  definition: PicrossDefinition,
  progress: PicrossProgress,
): PicrossSubmission {
  return {
    rows: resizeRows(progress.rows, definition.rows, definition.cols).map((row) =>
      row.replaceAll(CROSSED, EMPTY),
    ),
  };
}

export function isBlank(rows: string[]): boolean {
  return rows.every((row) => !row.includes(FILLED));
}
